import type { TextBevelDepth } from '../../types';
import { isValidTextEffectBlendMode } from '../../shared/blendModes';
import { GLOSS_CONTOURS } from './textEffectContours';

const finite = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);
const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));
const color = (value: unknown): value is string =>
  typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);

// Version-2 limits in document pixels; legacy Depth keeps its own bounds.
export const BEVEL_MAX_SIZE = 32;
export const BEVEL_MAX_SOFTNESS = 16;

// Experimental reference subset, not a Photoshop file interpreter. Limits are
// implementation bounds; they do not claim equivalence to Photoshop's ranges.
export function getBevelDepth(value: TextBevelDepth) {
  if (
    value.style !== 'inner-bevel' ||
    !['chisel-hard', 'smooth'].includes(value.technique) ||
    !GLOSS_CONTOURS.includes(value.glossContour) ||
    !isValidTextEffectBlendMode(value.highlight?.blendMode) ||
    !isValidTextEffectBlendMode(value.shadow?.blendMode) ||
    !color(value.highlight?.color) ||
    !color(value.shadow?.color) ||
    typeof value.contour?.enabled !== 'boolean' ||
    !['linear', 'ring'].includes(value.contour?.profile) ||
    ![
      value.size,
      value.depth,
      value.softness,
      value.lightDirection,
      value.altitude,
      value.highlight?.opacity,
      value.shadow?.opacity,
      value.contour?.range,
    ].every(finite)
  )
    return null;
  const size = clamp(value.size, 0, BEVEL_MAX_SIZE);
  const depth = clamp(value.depth, 0, 1000);
  const highlightOpacity = clamp(value.highlight.opacity, 0, 100) / 100;
  const shadowOpacity = clamp(value.shadow.opacity, 0, 100) / 100;
  if (!size || !depth || (!highlightOpacity && !shadowOpacity)) return null;
  return {
    technique: value.technique,
    documentSize: size,
    documentSoftness: clamp(value.softness, 0, BEVEL_MAX_SOFTNESS),
    renderScale: 1,
    size,
    surfaceScale: ((size * depth) / 100) * (value.mode === 'recessed' ? -1 : 1),
    softness: clamp(value.softness, 0, BEVEL_MAX_SOFTNESS),
    // SVG azimuth is measured from the positive x axis in screen coordinates.
    azimuth: (((value.lightDirection - 90) % 360) + 360) % 360,
    altitude: clamp(value.altitude, 0, 90),
    highlightColor: value.highlight.color,
    highlightOpacity,
    highlightBlendMode: value.highlight.blendMode,
    shadowColor: value.shadow.color,
    shadowOpacity,
    shadowBlendMode: value.shadow.blendMode,
    glossContour: value.glossContour,
    contour: value.contour.enabled ? value.contour.profile : 'linear',
    contourRange: value.contour.enabled
      ? clamp(value.contour.range, 1, 100)
      : 100,
  };
}
export type BevelRenderSettings = NonNullable<ReturnType<typeof getBevelDepth>>;

// Ring control points read from the supplied silver reference (0..255).
const RING = [
  [0, 0],
  [32, 110],
  [64, 237],
  [96, 239],
  [128, 138],
  [159, 32],
  [191, 6],
  [223, 99],
  [255, 255],
];
export function bevelContourTable(profile: string, range: number) {
  return Array.from({ length: 257 }, (_, index) => {
    const x = Math.min(255, ((index / 256) * 255 * 100) / range);
    if (profile === 'linear') return x / 255;
    const end = RING.findIndex(([point]) => point >= x);
    if (end <= 0) return 0;
    const [x0, y0] = RING[end - 1];
    const [x1, y1] = RING[end];
    return (y0 + ((y1 - y0) * (x - x0)) / (x1 - x0)) / 255;
  }).join(' ');
}
